import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CustomLoader from '../../components/CustomLoader';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { requestForm } from '../../store/actions/authActions';
import heroBg from '../../images/Banner/request_bg.png';
import './style.scss';

const FIELDS = [
    { name: 'name', label: 'Full Name', type: 'text', required: true, icon: '👤', col: 6 },
    { name: 'businessName', label: 'Business / Company Name', type: 'text', required: false, icon: '🏢', col: 6 },
    { name: 'email', label: 'Email Address', type: 'email', required: true, icon: '✉️', col: 6 },
    { name: 'phone', label: 'Phone Number', type: 'tel', required: false, icon: '📞', col: 6 },
    { name: 'quantity', label: 'Quantity Required', type: 'text', required: true, icon: '📦', col: 6 },
];

const RequestFormInner = () => {
    const push = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [socials, setSocials] = useState({});
    const preselectedProduct = location.state?.productName || '';
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        quantity: '',
        product: preselectedProduct,
        businessName: '',
        description: ''
    });
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    const getProduct = async () => {
        try {
            const response = await axios.get(process.env.REACT_APP_API_BASE_URL + '/products');
            const sorted = [...response.data].sort((a, b) => a.name.localeCompare(b.name));
            setProducts(sorted);
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };

    useEffect(() => {
        getProduct();
        axios.get(process.env.REACT_APP_API_BASE_URL + '/socials')
            .then(r => { if (r.data && r.data[0]) { const { _id, updatedAt, ...links } = r.data[0]; setSocials(links); } })
            .catch(() => {});
    }, []);

    const contactEmail = socials['gmail'] || 'info@deepprabha.com';
    const contactPhones = (socials['phones'] || [])
        .flatMap(p => p.split(','))
        .map(p => p.trim())
        .filter(Boolean);

    useEffect(() => {
        if (preselectedProduct && products.length > 0) {
            const match = products.find(p => p.name === preselectedProduct);
            if (match) setFormData(prev => ({ ...prev, product: match.name }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products]);

    const validate = (data) => {
        const errs = {};
        if (!data.name.trim()) errs.name = 'Full name is required';
        if (!data.email.trim()) errs.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(data.email)) errs.email = 'Enter a valid email';
        if (!data.quantity.trim()) errs.quantity = 'Quantity is required';
        if (!data.product) errs.product = 'Please select a product';
        if (!data.description.trim()) errs.description = 'Please describe your requirement';
        return errs;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (touched[name]) {
            const errs = validate({ ...formData, [name]: value });
            setErrors(prev => ({ ...prev, [name]: errs[name] }));
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
        const errs = validate(formData);
        setErrors(prev => ({ ...prev, [name]: errs[name] }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const allTouched = Object.fromEntries(Object.keys(formData).map(k => [k, true]));
        setTouched(allTouched);
        const errs = validate(formData);
        setErrors(errs);
        if (Object.keys(errs).length > 0) return;
        setLoading(true);
        try {
            dispatch(requestForm(formData, push, setLoading));
        } catch (error) {
            console.error('Error submitting form:', error);
            setLoading(false);
        }
    };

    const completionPercent = () => {
        const required = ['name', 'email', 'quantity', 'product', 'description'];
        const filled = required.filter(k => formData[k] && formData[k].toString().trim());
        return Math.round((filled.length / required.length) * 100);
    };

    return (
        <div className="dp-request">
            <CustomLoader show={loading} />

            {/* ── HERO ── */}
            <section className="dp-request-hero">
                <div
                    className="dp-request-hero__bg"
                    style={{ backgroundImage: `url(${heroBg})` }}
                />
                <div className="dp-request-hero__overlay" />
                <div className="dp-request-hero__content">
                    <span className="dp-request-hero__tag">Get In Touch</span>
                    <h1 className="dp-request-hero__title">Request a Product</h1>
                    <p className="dp-request-hero__sub">
                        Tell us what you need — our team will respond within 24 hours with pricing and availability.
                    </p>
                    <div className="dp-request-hero__badges">
                        <span>⚡ Fast Response</span>
                        <span>🔒 Secure & Confidential</span>
                        <span>🌍 Global Delivery</span>
                    </div>
                </div>
                <div className="dp-request-hero__wave">
                    <svg viewBox="0 0 1440 80" preserveAspectRatio="none">
                        <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#f4f6fb" />
                    </svg>
                </div>
            </section>

            {/* ── FORM SECTION ── */}
            <section className="dp-request-section">
                <div className="dp-request-layout">

                    {/* Left: info panel */}
                    <div className="dp-request-info">
                        <div className="dp-request-info__card">
                            <div className="dp-request-info__icon">🧪</div>
                            <h3>100+ Chemicals</h3>
                            <p>Solvents, acids, amines, reagents and specialty compounds for every industry.</p>
                        </div>
                        <div className="dp-request-info__card">
                            <div className="dp-request-info__icon">🚚</div>
                            <h3>Reliable Logistics</h3>
                            <p>Global shipping with full compliance documentation and timely delivery.</p>
                        </div>
                        <div className="dp-request-info__card">
                            <div className="dp-request-info__icon">💬</div>
                            <h3>Expert Support</h3>
                            <p>Our technical team is available to advise on product selection and specifications.</p>
                        </div>
                        <div className="dp-request-info__contact">
                            <h4>Direct Contact</h4>
                            <a href={`mailto:${contactEmail}`}>📧 {contactEmail}</a>
                            {contactPhones.map((ph, i) => (
                                <a key={i} href={`tel:${ph.replace(/\s/g, '')}`}>📞 {ph}</a>
                            ))}
                        </div>
                    </div>

                    {/* Right: form */}
                    <div className="dp-request-form-wrap">
                        <div className="dp-request-form-header">
                            <h2>Submit Your Request</h2>
                            <p>Fill in the details below and we'll get back to you shortly.</p>
                            {/* Progress bar */}
                            <div className="dp-request-progress">
                                <div className="dp-request-progress__bar">
                                    <div
                                        className="dp-request-progress__fill"
                                        style={{ width: `${completionPercent()}%` }}
                                    />
                                </div>
                                <span>{completionPercent()}% complete</span>
                            </div>
                        </div>

                        <form className="dp-request-form" onSubmit={handleSubmit} noValidate>
                            <div className="dp-request-form__grid">
                                {FIELDS.map(field => (
                                    <div
                                        key={field.name}
                                        className={`dp-request-form__field dp-request-form__field--col${field.col}${errors[field.name] && touched[field.name] ? ' has-error' : ''}${formData[field.name] && !errors[field.name] ? ' is-valid' : ''}`}
                                    >
                                        <label htmlFor={field.name}>
                                            {field.label}
                                            {field.required && <span className="dp-request-form__required">*</span>}
                                        </label>
                                        <div className="dp-request-form__input-wrap">
                                            <span className="dp-request-form__icon">{field.icon}</span>
                                            <input
                                                id={field.name}
                                                name={field.name}
                                                type={field.type}
                                                value={formData[field.name]}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder={field.label}
                                                required={field.required}
                                            />
                                        </div>
                                        {errors[field.name] && touched[field.name] && (
                                            <span className="dp-request-form__error">{errors[field.name]}</span>
                                        )}
                                    </div>
                                ))}

                                {/* Product select */}
                                <div className={`dp-request-form__field dp-request-form__field--col6${errors.product && touched.product ? ' has-error' : ''}${formData.product && !errors.product ? ' is-valid' : ''}`}>
                                    <label htmlFor="product">
                                        Product Required
                                        <span className="dp-request-form__required">*</span>
                                    </label>
                                    <div className="dp-request-form__input-wrap dp-request-form__input-wrap--select">
                                        <span className="dp-request-form__icon">🔬</span>
                                        <select
                                            id="product"
                                            name="product"
                                            value={formData.product}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        >
                                            <option value="">-- Select a product --</option>
                                            {products.map((p) => (
                                                <option key={p._id} value={p.name}>{p.name}</option>
                                            ))}
                                        </select>
                                        <span className="dp-request-form__chevron">▾</span>
                                    </div>
                                    {errors.product && touched.product && (
                                        <span className="dp-request-form__error">{errors.product}</span>
                                    )}
                                </div>

                                {/* Description */}
                                <div className={`dp-request-form__field dp-request-form__field--col12${errors.description && touched.description ? ' has-error' : ''}${formData.description && !errors.description ? ' is-valid' : ''}`}>
                                    <label htmlFor="description">
                                        Additional Requirements
                                        <span className="dp-request-form__required">*</span>
                                    </label>
                                    <div className="dp-request-form__input-wrap">
                                        <textarea
                                            id="description"
                                            name="description"
                                            rows={4}
                                            value={formData.description}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Describe your requirements, specifications, delivery timeline, etc."
                                        />
                                    </div>
                                    {errors.description && touched.description && (
                                        <span className="dp-request-form__error">{errors.description}</span>
                                    )}
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="dp-request-form__submit"
                                disabled={loading}
                            >
                                {loading ? (
                                    <span className="dp-request-form__submit-loading">
                                        <span className="dp-spinner" />
                                        Submitting...
                                    </span>
                                ) : (
                                    <>Send Request <span>→</span></>
                                )}
                            </button>

                            <p className="dp-request-form__disclaimer">
                                By submitting this form, you agree to be contacted by our team regarding your enquiry. Your data is handled in accordance with our privacy policy.
                            </p>
                        </form>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default RequestFormInner;
